import _ from "lodash";
import { useEffect, useCallback, useMemo, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { filterStateToGQL } from "../OmniFilter/hooks";
import { GQL } from "../../types/GQL";

interface IStreamQuery {
  key: string;
  size: number;
  sort: any;
  reverseSort: any;
  filter: any;
  enabled: boolean;
  refreshCursor: any;
  setRefreshCursor: any;
  queryFn: any;
  setHits: any;
}

export const useStreamQuery = ({
  key,
  size,
  sort,
  filter,
  enabled,
  reverseSort,
  refreshCursor,
  setRefreshCursor,
  queryFn,
  setHits,
}: IStreamQuery) => {
  const [scrollIndex, setScrollIndex] = useState<undefined | number>(undefined);
  const [startCursor, setStartCursor] = useState();
  const [initialRefreshCursor, setInitialRefreshCursor] = useState(refreshCursor);
  const tempFilter = {
    ...filter,
    permits_type_id: filter?.types?.length > 0 ? filter?.types?.map((item: string) => parseInt(item)) : undefined,
    renewal_status: filter?.renewal_status?.length > 0 ? filter?.renewal_status : undefined,
    submitted_at: filter?.submitted_at?.length > 0 ? filter?.submitted_at[0] : undefined,
    expired_at: filter?.expired_at?.length > 0 ? filter?.expired_at[0] : undefined,
    primary_identifier: filter?.primary_identifier ? filter?.primary_identifier[0] : undefined,
    application_id: filter?.application_id ? filter?.application_id[0] : undefined,
    permit_number: filter?.permit_number ? filter?.permit_number[0] : undefined,
    applicant: filter?.applicant ? filter?.applicant[0] : undefined,
    has_balance: filter?.has_balance?.length > 0 ? filter?.has_balance[0] : undefined,
  };
  let parsedValue = tempFilter.submitted_at;
  if ((!parsedValue?.from || parsedValue?.from === "") && (!parsedValue?.to || parsedValue?.to === "")) {
    delete tempFilter.submitted_at;
  }
  parsedValue = tempFilter.expired_at;
  if ((!parsedValue?.from || parsedValue?.from === "") && (!parsedValue?.to || parsedValue?.to === "")) {
    delete tempFilter.expired_at;
  }
  delete tempFilter.types;

  // search parameters
  const searchParams = useMemo(() => {
    return { size, sort, searchAfter: initialRefreshCursor, ...tempFilter };
  }, [size, sort, filter, initialRefreshCursor, refreshCursor]);
  const searchParamsReverse = useMemo(() => ({ size, sort: reverseSort, ...tempFilter }), [size, reverseSort, filter]);

  // main query (refetches whenever search parameters change)
  const recordsQuery = useInfiniteQuery([key, searchParams], queryFn, {
    enabled,
    getFetchMore: (lastGroup: any) => {
      const hits = _.get(lastGroup, "hits.hits", []);
      const nextCursor = _.get(_.last(hits), "sort");
      return nextCursor;
    },
  });
  const recordsQueryReverse = useInfiniteQuery([key, searchParamsReverse], queryFn, {
    enabled: false,
    getFetchMore: (lastGroup: any) => {
      const hits = _.get(lastGroup, "hits.hits", []);
      const nextCursor = _.get(_.last(hits), "sort");
      return nextCursor;
    },
  });
  const reverseHits = useMemo(() => {
    return _.chain(recordsQueryReverse.data || [])
      .map((page: any) => _.get(page, "hits.hits"))
      .flatten()
      .reverse()
      .compact()
      .value()
      .filter((hit: any) => {
        return (
          tempFilter.permits_type_id === undefined ||
          tempFilter.permits_type_id.length === 0 ||
          tempFilter.permits_type_id?.filter((item: any) => item === hit._source.permits_type_id).length !== 0
        );
      });
  }, [recordsQueryReverse.data]);
  const forwardHits = useMemo(() => {
    return _.chain(recordsQuery.data || [])
      .map((page: any) => _.get(page, "hits.hits"))
      .flatten()
      .compact()
      .value()
      .filter((hit: any) => {
        return (
          tempFilter.permits_type_id === undefined ||
          tempFilter.permits_type_id.length === 0 ||
          tempFilter.permits_type_id?.filter((item: any) => item === hit._source.permits_type_id).length !== 0
        );
      });
  }, [recordsQuery.data]);
  const hitsWindow = useMemo(() => {
    return _.chain([...reverseHits, ...forwardHits])
      .compact()
      .uniqBy((hit: any) => hit._id)
      .value();
  }, [reverseHits, forwardHits]);
  const scrollTop = useCallback(async () => {
    await recordsQuery.clear();
    await recordsQueryReverse.clear();
    setScrollIndex(0);
    setStartCursor(undefined);
    setRefreshCursor([]);
    setInitialRefreshCursor([]);
  }, [
    recordsQuery.clear,
    recordsQueryReverse.clear,
    setScrollIndex,
    setStartCursor,
    setRefreshCursor,
    setInitialRefreshCursor,
  ]);
  const totalHits = useMemo(() => _.get(recordsQuery.data, "[0].hits.total"), [recordsQuery.data]);
  const lastSearchTimeMs = useMemo(() => _.get(recordsQuery.data, "[0].took"), [recordsQuery.data]);
  const fetchUpstream = async () => {
    if (recordsQueryReverse.isLoading) {
      return () => {
        return;
      };
    }
    if (recordsQueryReverse.canFetchMore === undefined) {
      return await recordsQueryReverse.fetchMore(startCursor);
    }
    if (recordsQueryReverse.canFetchMore === false) {
      return;
    }
    return await recordsQueryReverse.fetchMore();
  };
  const fetchDownstream = async () => {
    if (recordsQuery.isLoading) {
      return () => {
        return;
      };
    }
    return await recordsQuery.fetchMore();
  };

  useEffect(() => {
    setHits(hitsWindow);
  }, [hitsWindow]);
  // Set start cursor, which allows the upstream cursor to be used
  useEffect(() => {
    if (!recordsQuery.isInitialData) {
      setStartCursor(_.get(_.first(hitsWindow), "sort", []));
    }
  }, [hitsWindow, setStartCursor, recordsQuery.isInitialData]);

  // Fetch upstream when ready
  useEffect(() => {
    if (enabled && recordsQueryReverse.isInitialData && startCursor) {
      fetchUpstream();
    }
  }, [enabled, startCursor, recordsQueryReverse.isInitialData]);

  // Set scroll index when upstream completes
  useEffect(() => {
    if (recordsQueryReverse.isSuccess) {
      const nextScrollIndex = _.get(_.last(recordsQueryReverse.data), "hits.hits", []).length;
      setScrollIndex(nextScrollIndex);
    }
  }, [recordsQueryReverse.data, setScrollIndex]);

  return {
    isLoading: recordsQuery.isLoading || recordsQueryReverse.isLoading,
    hitsWindow,
    enabled,
    scrollTop,
    totalHits,
    lastSearchTimeMs,
    fetchUpstream,
    fetchDownstream,
    refreshCursor,
    setRefreshCursor,
    startCursor,
    setStartCursor,
    scrollIndex,
    setScrollIndex,
    recordsQuery,
    recordsQueryReverse,
    searchParams,
  };
};

interface IUseGqlStreamQuery {
  key: string;
  size: number;
  sort: any;
  reverseSort: any;
  filter: any;
  enabled: boolean;
  refreshCursor: any;
  setRefreshCursor: any;
  queryFn: any;
  hits: any;
  setHits: any;
  gqlBuilder?: ({
    size,
    cursor,
    sort,
    filters,
  }: {
    size: number;
    cursor: number;
    sort: any;
    filters: { cityId: string; [field: string]: any };
  }) => GQL;
}

export const useGqlStreamQuery = ({
  key,
  size,
  sort,
  filter,
  enabled,
  refreshCursor,
  setRefreshCursor,
  queryFn,
  hits,
  setHits,
  gqlBuilder,
}: IUseGqlStreamQuery) => {
  const [scrollIndex, setScrollIndex] = useState<undefined | number>(undefined);
  const [startCursor, setStartCursor] = useState();
  const [initialRefreshCursor, setInitialRefreshCursor] = useState(refreshCursor);

  const searchParams = useMemo(() => {
    if (gqlBuilder) return { gql: gqlBuilder({ size, cursor: initialRefreshCursor, sort, filters: filter }) };
    return { gql: filterStateToGQL(filter, size, initialRefreshCursor, sort) };
  }, [size, sort, filter, initialRefreshCursor]);

  const recordsQuery = useInfiniteQuery([key, searchParams], queryFn, {
    enabled,
    getFetchMore: (lastGroup: any) => {
      const hits = _.get(lastGroup, "hits.hits", []);
      const nextCursor = _.get(_.last(hits), "sort");
      return nextCursor;
    },
    retry: true,
    staleTime: Infinity,
  });

  const forwardHits = useMemo(() => {
    const tempDatas = _.chain(recordsQuery.data || [])
      .map((page: any) => {
        return _.get(page, "hits.hits");
      })
      .flatten()
      .compact()
      .value();
    const tempHits = [...hits];
    tempDatas.forEach((item: any) => {
      const foundItem = tempHits.find((element: any) => element._id === item._id);
      if (!foundItem) {
        tempHits.push(item);
      }
    });
    return tempHits;
  }, [recordsQuery.data]);
  const scrollTop = useCallback(async () => {
    await recordsQuery.clear();
    setScrollIndex(0);
    setStartCursor(undefined);
    setRefreshCursor(0);
    setInitialRefreshCursor(0);
  }, [recordsQuery.clear, setScrollIndex, setStartCursor, setRefreshCursor, setInitialRefreshCursor]);
  const totalHits = useMemo(() => _.get(recordsQuery.data, "[0].hits.total"), [recordsQuery.data]);
  const lastSearchTimeMs = useMemo(() => _.get(recordsQuery.data, "[0].took"), [recordsQuery.data]);
  const fetchDownstream = async () => {
    if (recordsQuery.isLoading) {
      return () => {
        return;
      };
    }
    setInitialRefreshCursor(refreshCursor);
    return await recordsQuery.fetchMore();
  };
  useEffect(() => {
    if (hits.length <= forwardHits.length) {
      setHits(forwardHits);
    }
  }, [forwardHits]);
  useEffect(() => {
    recordsQuery.fetchMore();
  }, [filter]);
  // resets cursor to 0 if hits is reset
  useEffect(() => {
    if (hits.length === 0) setInitialRefreshCursor(0);
  }, [hits]);
  return {
    isLoading: recordsQuery.isLoading,
    hitsWindow: forwardHits,
    enabled,
    scrollTop,
    totalHits,
    lastSearchTimeMs,
    fetchDownstream,
    refreshCursor,
    setRefreshCursor,
    startCursor,
    setStartCursor,
    scrollIndex,
    setScrollIndex,
    recordsQuery,
    searchParams,
  };
};

interface IUseCustomStreamQuery {
  key: string;
  size: number;
  sort: any;
  reverseSort: any;
  filter: any;
  enabled: boolean;
  refreshCursor: any;
  setRefreshCursor: any;
  queryFn: any;
  hits: any;
  setHits: any;
  searchParamsBuilder: ({
    size,
    cursor,
    sort,
    filters,
  }: {
    size: number;
    cursor: number;
    sort: any;
    filters: { cityId: string; [field: string]: any };
  }) => any;
  getForwardHits?: (recordsQuery: any) => any;
  getTotalHits?: (recordsQuery: any) => any;
  getFetchMore?: (recordsQuery: any) => any;
}

export const useCustomStreamQuery = ({
  key,
  size,
  sort,
  filter,
  enabled,
  refreshCursor,
  setRefreshCursor,
  queryFn,
  hits,
  setHits,
  searchParamsBuilder,
  getForwardHits,
  getTotalHits,
  getFetchMore,
}: IUseCustomStreamQuery) => {
  const [scrollIndex, setScrollIndex] = useState<undefined | number>(undefined);
  const [startCursor, setStartCursor] = useState();
  const [initialRefreshCursor, setInitialRefreshCursor] = useState(refreshCursor);

  const searchParams = useMemo(() => {
    return searchParamsBuilder({ size, cursor: initialRefreshCursor, sort, filters: filter });
  }, [size, sort, filter, initialRefreshCursor]);

  const recordsQuery = useInfiniteQuery([key, searchParams], queryFn, {
    enabled,
    retry: true,
    cacheTime: 0, // Don't keep the data if the component is unmounted, so that we can re-fetch it on mount
    getFetchMore,
    queryFnParamsFilter(args) {
      return [args[1]];
    },
  });

  const forwardHits = useMemo(() => {
    if (!!getForwardHits) {
      return getForwardHits(recordsQuery);
    }
    return recordsQuery.data;
  }, [recordsQuery.data]);

  const scrollTop = useCallback(() => {
    recordsQuery.clear();
    setScrollIndex(0);
    setStartCursor(undefined);
    setRefreshCursor(0);
    setInitialRefreshCursor(0);
  }, [recordsQuery.clear, setScrollIndex, setStartCursor, setRefreshCursor, setInitialRefreshCursor]);

  const totalHits = useMemo(() => {
    if (!!getTotalHits) {
      return getTotalHits(recordsQuery);
    }
    return recordsQuery.data?.length;
  }, [recordsQuery.data]);

  const fetchDownstream = async () => {
    if (recordsQuery.isLoading) {
      return () => {
        return;
      };
    }
    setInitialRefreshCursor(refreshCursor);
    return await recordsQuery.fetchMore();
  };

  useEffect(() => {
    if (hits.length <= forwardHits?.length) {
      setHits(forwardHits);
    }
  }, [forwardHits]);

  useEffect(() => {
    recordsQuery.fetchMore();
  }, [filter]);

  // resets cursor to 0 if hits is reset
  useEffect(() => {
    if (hits.length === 0) setInitialRefreshCursor(0);
  }, [hits]);

  return {
    isLoading: recordsQuery.isLoading,
    hitsWindow: forwardHits,
    enabled,
    scrollTop,
    totalHits,
    fetchDownstream,
    refreshCursor,
    setRefreshCursor,
    startCursor,
    setStartCursor,
    scrollIndex,
    setScrollIndex,
    recordsQuery,
    searchParams,
  };
};

export const useRenewalStreamQuery = ({
  key,
  size,
  sort,
  filter,
  enabled,
  reverseSort,
  refreshCursor,
  setRefreshCursor,
  queryFn,
  setHits,
}: IStreamQuery) => {
  const [scrollIndex, setScrollIndex] = useState<undefined | number>(undefined);
  const [startCursor, setStartCursor] = useState();
  const [initialRefreshCursor, setInitialRefreshCursor] = useState(refreshCursor);
  const tempFilter = {
    ...filter,
    renewal_status: filter?.renewal_status?.length > 0 ? filter?.renewal_status : undefined,
    submitted_at: filter?.submitted_at?.length > 0 ? filter?.submitted_at[0] : undefined,
    expired_at: filter?.expired_at?.length > 0 ? filter?.expired_at[0] : undefined,
    primary_identifier: filter?.primary_identifier ? filter?.primary_identifier[0] : undefined,
    application_id: filter?.application_id ? filter?.application_id[0] : undefined,
    permit_number: filter?.permit_number ? filter?.permit_number[0] : undefined,
    applicant: filter?.applicant ? filter?.applicant[0] : undefined,
    has_balance: filter?.has_balance?.length > 0 ? filter?.has_balance[0] : undefined,
    is_issued: "true",
  };
  let parsedValue = tempFilter.submitted_at;
  if ((!parsedValue?.from || parsedValue?.from === "") && (!parsedValue?.to || parsedValue?.to === "")) {
    delete tempFilter.submitted_at;
  }
  parsedValue = tempFilter.expired_at;
  if ((!parsedValue?.from || parsedValue?.from === "") && (!parsedValue?.to || parsedValue?.to === "")) {
    delete tempFilter.expired_at;
  }
  delete tempFilter.types;
  const searchParams = useMemo(() => {
    return { size, sort, searchAfter: initialRefreshCursor, ...tempFilter };
  }, [size, sort, filter, initialRefreshCursor]);
  const searchParamsReverse = useMemo(() => ({ size, sort: reverseSort, ...tempFilter }), [size, reverseSort, filter]);
  const recordsQuery = useInfiniteQuery([key, searchParams], queryFn, {
    enabled,
    getFetchMore: (lastGroup: any) => {
      const hits = _.get(lastGroup, "hits.hits", []);
      const nextCursor = _.get(_.last(hits), "sort");
      return nextCursor;
    },
  });
  const recordsQueryReverse = useInfiniteQuery([key, searchParamsReverse], queryFn, {
    enabled: false,
    getFetchMore: (lastGroup: any) => {
      const hits = _.get(lastGroup, "hits.hits", []);
      const nextCursor = _.get(_.last(hits), "sort");
      return nextCursor;
    },
  });
  const reverseHits = useMemo(() => {
    return _.chain(recordsQueryReverse.data || [])
      .map((page: any) => _.get(page, "hits.hits"))
      .flatten()
      .reverse()
      .compact()
      .value()
      .filter((hit: any) => {
        return tempFilter.permits_type_id === hit._source.permits_type_id;
      });
  }, [recordsQueryReverse.data]);
  const forwardHits = useMemo(() => {
    return _.chain(recordsQuery.data || [])
      .map((page: any) => _.get(page, "hits.hits"))
      .flatten()
      .compact()
      .value()
      .filter((hit: any) => {
        return tempFilter.permits_type_id === hit._source.permits_type_id;
      });
  }, [recordsQuery.data]);
  const hitsWindow = useMemo(() => {
    return _.chain([...reverseHits, ...forwardHits])
      .compact()
      .uniqBy((hit: any) => hit._id)
      .value();
  }, [reverseHits, forwardHits]);
  const scrollTop = useCallback(async () => {
    await recordsQuery.clear();
    await recordsQueryReverse.clear();
    setScrollIndex(0);
    setStartCursor(undefined);
    setRefreshCursor([]);
    setInitialRefreshCursor([]);
  }, [
    recordsQuery.clear,
    recordsQueryReverse.clear,
    setScrollIndex,
    setStartCursor,
    setRefreshCursor,
    setInitialRefreshCursor,
  ]);
  const totalHits = useMemo(() => _.get(recordsQuery.data, "[0].hits.total"), [recordsQuery.data]);
  const lastSearchTimeMs = useMemo(() => _.get(recordsQuery.data, "[0].took"), [recordsQuery.data]);
  const fetchUpstream = async () => {
    if (recordsQueryReverse.isLoading) {
      return () => {
        return;
      };
    }
    if (recordsQueryReverse.canFetchMore === undefined) {
      return await recordsQueryReverse.fetchMore(startCursor);
    }
    if (recordsQueryReverse.canFetchMore === false) {
      return;
    }
    return await recordsQueryReverse.fetchMore();
  };
  const fetchDownstream = async () => {
    if (recordsQuery.isLoading) {
      return () => {
        return;
      };
    }
    // setInitialRefreshCursor(refreshCursor);
    return await recordsQuery.fetchMore();
  };

  useEffect(() => {
    setHits(hitsWindow);
  }, [hitsWindow]);
  // Set start cursor, which allows the upstream cursor to be used
  useEffect(() => {
    if (!recordsQuery.isInitialData) {
      setStartCursor(_.get(_.first(hitsWindow), "sort", []));
    }
  }, [hitsWindow, setStartCursor, recordsQuery.isInitialData]);

  // Fetch upstream when ready
  useEffect(() => {
    if (enabled && recordsQueryReverse.isInitialData && startCursor) {
      fetchUpstream();
    }
  }, [enabled, startCursor, recordsQueryReverse.isInitialData]);

  // Set scroll index when upstream completes
  useEffect(() => {
    if (recordsQueryReverse.isSuccess) {
      const nextScrollIndex = _.get(_.last(recordsQueryReverse.data), "hits.hits", []).length;
      setScrollIndex(nextScrollIndex);
    }
  }, [recordsQueryReverse.data, setScrollIndex]);

  return {
    isLoading: recordsQuery.isLoading || recordsQueryReverse.isLoading,
    hitsWindow,
    enabled,
    scrollTop,
    totalHits,
    lastSearchTimeMs,
    fetchUpstream,
    fetchDownstream,
    refreshCursor,
    setRefreshCursor,
    startCursor,
    setStartCursor,
    scrollIndex,
    setScrollIndex,
    recordsQuery,
    recordsQueryReverse,
    searchParams,
  };
};
